import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  Autocomplete,
} from '@mui/material';
import LoadingButton from './LoadingButton';

export const CATEGORY_KINDS = [
  { value: 'spending', label: 'Spending', help: 'Counts towards your spending and budgets' },
  { value: 'income', label: 'Income', help: 'Money you earn' },
  { value: 'transfer', label: 'Transfer', help: 'Money moving between your own accounts (card bills, top-ups)' },
  { value: 'investment', label: 'Investment', help: 'SIPs, stocks, deposits and their redemptions' },
  { value: 'refund', label: 'Refund', help: 'Money coming back for something you bought' },
  { value: 'asset', label: 'Big purchase / asset', help: 'A home or car: kept out of monthly spending' },
];

export const categoryKindLabel = (kind) => CATEGORY_KINDS.find(k => k.value === kind)?.label || kind;

/**
 * Add or edit a category: name, what kind of money it is, its group and a
 * description (the description also guides the AI when it categorises).
 * Default categories can't be renamed.
 */
const CategoryDialog = ({ open, category, groups = [], onClose, onSave }) => {
  const [form, setForm] = useState({ name: '', kind: 'spending', groupName: '', description: '' });
  const [saving, setSaving] = useState(false);
  const isEdit = !!category;

  useEffect(() => {
    if (open) {
      setForm({
        name: category?.name || '',
        kind: category?.kind || 'spending',
        groupName: category?.groupName || '',
        description: category?.description || '',
      });
    }
  }, [open, category]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await onSave({ ...form, name: form.name.trim() });
    } finally {
      setSaving(false);
    }
  };

  const kindHelp = CATEGORY_KINDS.find(k => k.value === form.kind)?.help;
  const kindChanged = isEdit && category.kind && category.kind !== form.kind;
  const renamed = isEdit && category.name !== form.name.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{isEdit ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              autoFocus={!isEdit}
              disabled={isEdit && category.isDefault}
              helperText={isEdit && category.isDefault ? 'Default categories keep their name' : undefined}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Kind</InputLabel>
              <Select
                label="Kind"
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
              >
                {CATEGORY_KINDS.map(k => <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>)}
              </Select>
              {kindHelp && <FormHelperText>{kindHelp}</FormHelperText>}
            </FormControl>
            <Autocomplete
              freeSolo
              options={groups}
              value={form.groupName}
              onInputChange={(_, value) => setForm({ ...form, groupName: value })}
              renderInput={(params) => <TextField {...params} label="Group" placeholder="e.g. Food, Home, Travel" />}
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What goes here, e.g. Swiggy, Zomato, restaurants, cafes"
              helperText="Also helps the AI pick this category"
              multiline
              minRows={2}
              fullWidth
            />
            {(kindChanged || renamed) && (
              <Alert severity="info">
                {renamed && 'Transactions in this category are renamed too. '}
                {kindChanged && 'Transactions in this category change kind too, unless you set their kind yourself.'}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <LoadingButton type="submit" variant="contained" loading={saving} disabled={!form.name.trim()}>
            Save
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CategoryDialog;
